import type { OrganizationFrame, OrganizationUser } from "@kontextwerk/control-web-client"
import { getAdminApi } from "../client"
import {
  fetchOrganizationUserSettingsProjection,
  fetchUserSettings,
  type OrganizationUserSettingsProjection,
  type UserSettings,
} from "../user-settings"

export type OrganizationFrameLoadContext = {
  canViewOrganizationUsers: boolean
  currentUserId: string | null
  initialOrganization?: OrganizationFrame | null
}

type OrganizationUserSettingsItem = OrganizationUserSettingsProjection["items"][number]

export const mergeOrganizationFrameUserSettings = (
  organization: OrganizationFrame,
  settingsItems: OrganizationUserSettingsItem[],
): OrganizationFrame => {
  const users = organization.users ?? []

  if (!users.length || !settingsItems.length) {
    return { ...organization, users }
  }

  const frameUserIds = new Set(users.map((user) => user.id))
  const settingsByUserId = new Map<string, UserSettings>()
  for (const item of settingsItems) {
    if (!frameUserIds.has(item.user_id)) continue

    settingsByUserId.set(item.user_id, item.settings ?? {})
  }

  return {
    ...organization,
    users: users.map((user): OrganizationUser => ({
      ...user,
      ...(settingsByUserId.get(user.id) ?? {}),
    })),
  }
}

export const loadKehrblattOrganizationFrame = async (
  identifier: string,
  context: OrganizationFrameLoadContext,
): Promise<OrganizationFrame> => {
  const initial = context.initialOrganization
  const frame =
    initial && (initial.id === identifier || initial.slug === identifier)
      ? initial
      : await getAdminApi().organization.registry.getFrame(identifier)
  const users = frame.users ?? []
  const normalizedFrame: OrganizationFrame = { ...frame, users }

  if (!users.length) return normalizedFrame

  try {
    if (context.canViewOrganizationUsers) {
      const projection = await fetchOrganizationUserSettingsProjection(frame.id)

      return mergeOrganizationFrameUserSettings(normalizedFrame, projection.items ?? [])
    }

    const currentUserId = context.currentUserId

    if (!currentUserId || !users.some((user) => user.id === currentUserId)) {
      return normalizedFrame
    }

    const settings = await fetchUserSettings(currentUserId)

    return mergeOrganizationFrameUserSettings(normalizedFrame, [
      { user_id: currentUserId, settings },
    ])
  } catch {
    console.error("organization_frame_user_settings_enrichment_failed", {
      visibilityMode: context.canViewOrganizationUsers ? "organization" : "self",
      userCount: users.length,
    })

    return normalizedFrame
  }
}
